import { useState } from 'react';
import { CreditCard, Coins, AlertCircle, Download, Wallet, FileCheck2, Check } from 'lucide-react';
import { Money } from '@/shared/display/Money';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { StatCard, PageHeader } from '@/cajita/ui';
import { CxDialog } from '@/cajita/overlay';
import { useAccounts } from '@/features/treasury/treasury.api';
import { usePayables, usePaySupplier, type Payable } from './purchases.api';

const fmt = (n: number | string): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const AVP = '#8B5CF6';
const initials = (n: string): string =>
  (n || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

export function PayablesPage(): JSX.Element {
  const { data, isLoading } = usePayables();
  const [payTarget, setPayTarget] = useState<Payable | null>(null);

  const aging = data?.aging;
  const items = data?.items ?? [];
  const buckets: Array<{ label: string; value: string; color: string }> = aging
    ? [
        { label: 'Corriente', value: aging.current, color: '#74D7A8' },
        { label: '1–30 días', value: aging.d1_30, color: '#B9DE83' },
        { label: '31–60 días', value: aging.d31_60, color: '#F4C56E' },
        { label: '61–90 días', value: aging.d61_90, color: '#F6AE79' },
        { label: '90+ días', value: aging.d90, color: '#F1979A' },
      ]
    : [];
  const totalPorPagar = aging ? buckets.reduce((s, x) => s + Number(x.value), 0) : 0;
  const vencido = aging ? Number(aging.d1_30) + Number(aging.d31_60) + Number(aging.d61_90) + Number(aging.d90) : 0;

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Cuentas por pagar"
          sub="Obligaciones con proveedores · antigüedad (aging)"
          actions={
            <button className="btn btn-secondary">
              <Download size={17} />
              Exportar
            </button>
          }
        />

        <div className="grid grid-collapse" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
          <StatCard label="Total por pagar" value={fmt(totalPorPagar)} icon={<Coins size={19} />} accent="#F59E0B" sub={`${items.length} documentos`} />
          <StatCard label="Vencido" value={fmt(vencido)} icon={<AlertCircle size={19} />} accent="#EF4444" />
          <StatCard label="Documentos" value={String(items.length)} icon={<FileCheck2 size={19} />} accent="#3B82F6" />
        </div>

        {buckets.length > 0 && (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 20 }}>
            {buckets.map((x) => {
              const val = Number(x.value);
              return (
                <div key={x.label} className="card card-pad fade-in" style={{ borderTop: `3px solid ${x.color}` }}>
                  <div className="t-sm muted fw6">{x.label}</div>
                  <div className="t-h1 tnum fw8" style={{ marginTop: 8, color: val > 0 ? 'var(--ink)' : 'var(--muted-2)' }}>
                    {fmt(val)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="card">
          <div className="card-head">
            <div className="card-title">Detalle de documentos</div>
            <span className="t-sm muted">{items.length} pendientes</span>
          </div>
          {isLoading ? (
            <div style={{ padding: '8px 0' }}>
              {Array.from({ length: 6 }).map((_, r) => (
                <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
                  {Array.from({ length: 6 }).map((_, c) => (
                    <div key={c} className="sk" style={{ height: 12, flex: c === 0 ? 2 : 1, opacity: 1 - r * 0.1 }} />
                  ))}
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">
                <CreditCard size={28} />
              </div>
              <div className="muted">Sin deudas pendientes.</div>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th>Vence</th>
                    <th className="right">Total</th>
                    <th className="right">Pagado</th>
                    <th className="right">Saldo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((ap) => (
                    <tr key={ap.id}>
                      <td>
                        <div className="row aic gap10">
                          <span className="av" style={{ width: 30, height: 30, fontSize: 11, background: `${AVP}22`, color: AVP, borderColor: `${AVP}33` }}>
                            {initials(ap.supplierName)}
                          </span>
                          <div>
                            <div className="strong">{ap.supplierName}</div>
                            <div className="t-xs muted tnum">{ap.supplierDoc}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {ap.dueDate ? (
                          <>
                            <div className="tnum">
                              <DateDisplay value={ap.dueDate} />
                            </div>
                            {ap.daysOverdue > 0 && <div className="t-xs neg fw6">{ap.daysOverdue} días vencido</div>}
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="right tnum">
                        <Money value={ap.totalAmount} />
                      </td>
                      <td className="right tnum muted">
                        <Money value={ap.paidAmount} />
                      </td>
                      <td className="right tnum strong">
                        <Money value={ap.balance} />
                      </td>
                      <td className="right">
                        <button className="btn btn-secondary btn-sm" onClick={() => setPayTarget(ap)}>
                          <Wallet size={15} />
                          Pagar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {payTarget && <PayDialog payable={payTarget} onClose={() => setPayTarget(null)} />}
    </div>
  );
}

function PayDialog({ payable, onClose }: { payable: Payable; onClose: () => void }): JSX.Element {
  const pay = usePaySupplier();
  const { data: accounts } = useAccounts();
  const [amount, setAmount] = useState(payable.balance);
  const [method, setMethod] = useState('CASH');
  const [reference, setReference] = useState('');
  const [accountId, setAccountId] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    pay.mutate(
      { id: payable.id, amount: Number(amount), method, reference: reference || undefined, accountId: accountId || undefined },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <CxDialog
      title="Registrar pago"
      sub={payable.supplierName}
      icon={<Wallet size={20} />}
      iconTone="warning"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" type="submit" form="pay-form" disabled={pay.isPending || Number(amount) <= 0}>
            <Check size={18} />
            {pay.isPending ? 'Guardando…' : 'Registrar pago'}
          </button>
        </>
      }
    >
      <form id="pay-form" onSubmit={submit} className="col gap14">
        <div className="row between aic" style={{ padding: '12px 14px', background: 'var(--canvas)', borderRadius: 11 }}>
          <span className="muted fw6 t-sm">Saldo pendiente</span>
          <span className="tnum fw8" style={{ fontSize: 20 }}>
            <Money value={payable.balance} />
          </span>
        </div>
        <div className="field">
          <label className="label">Monto</label>
          <input className="input tnum" type="number" step="any" min="0" value={amount} required onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label className="label">Método</label>
            <select className="select" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="CASH">Efectivo</option>
              <option value="TRANSFER">Transferencia</option>
              <option value="CHECK">Cheque</option>
              <option value="CARD">Tarjeta</option>
              <option value="OTHER">Otro</option>
            </select>
          </div>
          <div className="field">
            <label className="label">Referencia</label>
            <input className="input" value={reference} placeholder="N° operación" onChange={(e) => setReference(e.target.value)} />
          </div>
        </div>
        {(accounts?.length ?? 0) > 0 && (
          <div className="field">
            <label className="label">Cuenta (de dónde sale)</label>
            <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">— No afectar tesorería —</option>
              {(accounts ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </form>
    </CxDialog>
  );
}
